/**
 * Waar de copiloot naar meekijkt (sessie 46, B6).
 *
 * Eén gesprek op alle pagina's: de schil houdt vast wát er in beeld is
 * (een kaart, een klant, een retour, een review of een pagina), en elke
 * pagina zegt dat met `useKijktMee`. De copiloot leest het en zet het in
 * zijn kop als "Kijkt mee op: ...". Het gesprek zelf blijft staan als JP
 * van pagina of kaart wisselt; alleen de context van de volgende vraag
 * verandert.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type KijkSoort = "zaak" | "klant" | "retour" | "review" | "pagina";

export interface KijkContext {
  soort: KijkSoort;
  /** Het id van de kaart, klant, retour of review; bij een pagina de naam. */
  id: string | null;
}

interface Waarde {
  context: KijkContext;
  zet: (context: KijkContext) => void;
}

const STANDAARD: KijkContext = { soort: "pagina", id: "vandaag" };

const Ctx = createContext<Waarde>({ context: STANDAARD, zet: () => {} });

export function KijktMeeProvider({ children }: { children: ReactNode }) {
  const [context, zet] = useState<KijkContext>(STANDAARD);
  const waarde = useMemo(() => ({ context, zet }), [context]);
  return <Ctx.Provider value={waarde}>{children}</Ctx.Provider>;
}

/** Wat er nu in beeld is. */
export function useKijkContext(): KijkContext {
  return useContext(Ctx).context;
}

/**
 * Zeg de copiloot waar hij naar kijkt. Een pagina roept dit aan met wat
 * er gekozen is, en valt terug op zijn eigen naam als er niets gekozen
 * is.
 */
export function useKijktMee(soort: KijkSoort, id: string | null, pagina: string): void {
  const { zet } = useContext(Ctx);
  useEffect(() => {
    if (id) zet({ soort, id });
    else zet({ soort: "pagina", id: pagina });
  }, [soort, id, pagina, zet]);
}
